/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import firebase from 'firebase';
import { useAtom } from 'jotai';
import { db } from '../../../shared/services/firebase';
import useConversationService from '../../../shared/services/conversationService';
import {
  currentOpenConversationMessagesAtom,
  userRecordAtom,
  unreadParentMessagesAtom,
  unreadTeacherMessagesAtom,
  allParentConversationsAtom,
  allTeachersConversationsAtom,
  pauseMessageListenerAtom,
} from '../../../shared/state/atoms';
import { FIRESTORE_FIELDS, FIRESTORE_KEYS, MESSAGE_TABS } from '../../../shared/utils/constants';
import { USER_ROLES } from '../../../shared/utils/roles';

const useAdminMessages = () => {
  const { addMessage, createConversation, updateConversation } = useConversationService();
  const [userRecord] = useAtom(userRecordAtom);
  const [, setUnreadParentMessages] = useAtom(unreadParentMessagesAtom);
  const [, setUnreadTeacherMessages] = useAtom(unreadTeacherMessagesAtom);
  const [allParentConversations, setAllParentsConversations] = useAtom(allParentConversationsAtom);
  const [allTeachersConversations, setAllTeachersConversations] = useAtom(allTeachersConversationsAtom);
  const [messageListenerIsPaused, setMessageListenerIsPaused] = useAtom(pauseMessageListenerAtom);
  const [currentOpenConversationMessages, setCurrentOpenConversationMessages] = useAtom(
    currentOpenConversationMessagesAtom
  );
  const [isLoadingMesssages, setIsLoadingMesssages] = useState(false);

  const { ASC, CLASS_ID, DESC, META } = FIRESTORE_FIELDS;
  const { IS_READ_BY_PARENT, IS_READ_BY_TEACHER, LAST_MESSAGE } = FIRESTORE_FIELDS.CONVERSATION;
  const { CONVERSATION, MESSAGES } = FIRESTORE_KEYS;
  const { CLASS, PARENT, TEACHER } = MESSAGE_TABS;
  const { ROLE_PARENT, ROLE_TECAHER } = USER_ROLES;

  const sendMessageToSingle = async (user, role, message, fileUrl) => {
    const isReadByType = role === ROLE_TECAHER ? IS_READ_BY_TEACHER : IS_READ_BY_PARENT;

    const conversationSnapshot = await db
      .collection(CONVERSATION)
      .where(`${role}.id`, '==', user?.id)
      .where('admin.id', '==', userRecord.id)
      .where(CLASS_ID, '==', `${user?.defaultClass?.id}`)
      .get();

    let conversationId;
    const conversationData = {
      classId: user?.defaultClass?.id || '',
      admin: {
        id: userRecord?.id,
        name: userRecord?.details?.name,
        photoUrl: userRecord?.photoUrl || '',
        token: userRecord?.token || '',
      },

      lastMessage: {
        isRead: false,
        [isReadByType]: false,
        text: message,
        token: user?.token || '',
        meta: {
          createdDate: firebase.firestore.Timestamp.now(),
          modifiedDate: firebase.firestore.Timestamp.now(),
        },
        user: {
          id: userRecord?.id,
          name: userRecord?.details?.name,
          photoUrl: userRecord?.photoUrl || '',
        },
      },

      [role]: {
        id: user.id,
        name: user?.details?.name || `${user?.details?.firstName} ${user?.details?.lastName}`,
        photoUrl: user?.details?.photoUrl || '',
        token: user?.token || '',
      },
    };
    if (conversationSnapshot.empty) {
      const newConversationRef = await createConversation(conversationData);
      conversationId = newConversationRef.id;
    } else {
      conversationId = conversationSnapshot.docs[0].id;
    }
    if (fileUrl) {
      const messageData = {
        isRead: false,
        [isReadByType]: false,
        meta: {
          createdDate: firebase.firestore.Timestamp.now(),
          modifiedDate: firebase.firestore.Timestamp.now(),
        },
        text: fileUrl,
        token: user?.token || '',
        user: {
          id: userRecord?.id,
          name: userRecord?.details?.name,
          photoUrl: userRecord?.photoUrl || '',
        },
      };
      await updateConversation(conversationId, {
        lastMessage: {
          isRead: false,
          [isReadByType]: false,
          text: fileUrl,
          token: user?.token || '',
          meta: { modifiedDate: firebase.firestore.Timestamp.now(), createdDate: firebase.firestore.Timestamp.now() },
          user: {
            id: userRecord?.id,
            name: userRecord?.details?.name,
            photoUrl: userRecord?.photoUrl || '',
          },
        },
      });
      await addMessage(conversationId, messageData);
    }
    if (message) {
      const messageData = {
        isRead: false,
        [isReadByType]: false,
        meta: {
          createdDate: firebase.firestore.Timestamp.now(),
          modifiedDate: firebase.firestore.Timestamp.now(),
        },
        text: message,
        token: user?.token || '',
        user: {
          id: userRecord?.id,
          name: userRecord?.details?.name,
          photoUrl: userRecord?.photoUrl || '',
        },
      };
      await updateConversation(conversationId, {
        lastMessage: {
          isRead: false,
          [isReadByType]: false,
          text: message,
          token: user?.token || '',
          meta: { modifiedDate: firebase.firestore.Timestamp.now(), createdDate: firebase.firestore.Timestamp.now() },
          user: {
            id: userRecord?.id,
            name: userRecord?.details?.name,
            photoUrl: userRecord?.photoUrl || '',
          },
        },
      });
      await addMessage(conversationId, messageData);
    }
  };

  const messageAllTeachers = async (teacherList, message, fileUrl) => {
    setMessageListenerIsPaused(true);
    try {
      const promises = teacherList.map(async (teacher) => {
        await sendMessageToSingle(teacher, ROLE_TECAHER, message, fileUrl);
      });
      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error('Failed to message all teachers:', error);
      return false;
    } finally {
      setMessageListenerIsPaused(false);
    }
  };

  const messageAllParents = async (parentList, message, fileUrl) => {
    setMessageListenerIsPaused(true);
    try {
      const promises = parentList.map(async (parent) => {
        await sendMessageToSingle(parent, ROLE_PARENT, message, fileUrl);
      });

      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error('Failed to message all parents:', error);
      return false;
    } finally {
      setMessageListenerIsPaused(false);
    }
  };

  const useParentConversationsByIds = (parentIds, classId, setCurrentParentsinClassMessage) => {
    const [parentConvos] = useAtom(allParentConversationsAtom);

    useEffect(() => {
      if (!parentIds || !classId || !parentConvos) return;

      const filteredParents = parentIds.map((parentId) => {
        const parent = parentConvos.find((p) => p.id === parentId);

        const conversation = parent?.conversation?.classId === classId ? parent.conversation : null;

        return {
          ...parent,
          conversation,
        };
      });

      setCurrentParentsinClassMessage(filteredParents);
    }, [parentIds, classId, parentConvos, setCurrentParentsinClassMessage]);
  };

  const useCurrentConversationListener = (conversationId) => {
    useEffect(() => {
      if (!conversationId) return;
      const unsubscribeMessages = db
        .collection(CONVERSATION)
        .doc(conversationId)
        .collection(MESSAGES)
        .orderBy(META.CREATED, ASC)
        .onSnapshot((snapshot) => {
          const messages = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          markUnreadMessagesAsRead(conversationId);
          setCurrentOpenConversationMessages(messages);
        });

      return () => {
        unsubscribeMessages();
      };
    }, [conversationId]);
  };

  const listenToUserConversations = (updateConversation) => {
    const unsubscribeConversations = db
      .collection(CONVERSATION)
      .where('admin.id', '==', userRecord.id)
      .orderBy(`${LAST_MESSAGE}.${META.CREATED}`, DESC)
      .onSnapshot(async (conversationSnapshot) => {
        try {
          const conversationPromises = conversationSnapshot.docs.map(async (doc) => {
            const conversationData = { id: doc.id, ...doc.data() };

            return new Promise((resolve) => {
              const unsubscribeMessages = db
                .collection(CONVERSATION)
                .doc(doc.id)
                .collection(MESSAGES)
                .orderBy(META.CREATED, ASC)
                .onSnapshot((messagesSnapshot) => {
                  const messages = messagesSnapshot.docs.map((msgDoc) => ({
                    id: msgDoc.id,
                    ...msgDoc.data(),
                  }));

                  const unread = messages.filter((msg) => msg.user?.id !== userRecord.id && !msg.isRead);

                  resolve({
                    ...conversationData,
                    messages,
                    unread,
                    unsubscribeMessages,
                  });
                });
            });
          });

          const updatedConversations = await Promise.all(conversationPromises);
          updateConversation(updatedConversations);
        } catch (error) {
          console.error('Error fetching real-time conversations:', error);
        }
      });

    return unsubscribeConversations;
  };

  const useGetAllParentTeacherAdminConversations = (parentList, teacherList) => {
    useEffect(() => {
      if (!messageListenerIsPaused) {
        const unsubscribeConversations = listenToUserConversations(updateParentTeacherConversations);

        return () => {
          unsubscribeConversations();
        };
      }
    }, [parentList, teacherList, messageListenerIsPaused]);

    // Function to update the state in atoms
    const updateParentTeacherConversations = (conversationsList) => {
      const parentConvos = parentList.map((parent) => {
        const conversation = conversationsList.find((conv) => conv.parent?.id === parent.id);
        return {
          ...parent,
          conversation: conversation || null,
          students:
            conversation && parent?.conversation !== null
              ? parent.students?.filter((student) => student.classId === conversation?.classId)
              : parent.students,
        };
      });

      const unreadParentConvos = parentConvos.filter((parent) => parent?.conversation?.unread.length > 0);
      setUnreadParentMessages(unreadParentConvos);
      setAllParentsConversations(parentConvos);

      const teacherConvos = teacherList.map((teacher) => {
        const conversation = conversationsList.find((conv) => conv.teacher?.id === teacher.id);
        return {
          ...teacher,
          conversation: conversation || null,
        };
      });
      const unreadTeacherConvos = teacherConvos.filter((teach) => teach?.conversation?.unread.length > 0);

      setUnreadTeacherMessages(unreadTeacherConvos);
      setAllTeachersConversations(teacherConvos);
    };
  };

  const getMessagesFromAtom = (id, currentTab) => {
    if (id) {
      let conversationWith = null;
      if (currentTab === PARENT || CLASS) conversationWith = allParentConversations;
      if (currentTab === TEACHER) conversationWith = allTeachersConversations;
      const currentConversation = conversationWith.find((conv) => conv?.conversation?.id === id);
      return currentConversation;
    }
    const emptyConvo = [];
    return emptyConvo;
  };

  const markUnreadMessagesAsRead = async (conversationId) => {
    try {
      const messagesSnapshot = await db
        .collection(CONVERSATION)
        .doc(conversationId)
        .collection(MESSAGES)
        .where('isRead', '==', false)
        .get();

      const unreadMessages = messagesSnapshot.docs.filter((doc) => doc.data().user.id !== userRecord.id);

      if (unreadMessages.length > 0) {
        const messageUpdates = unreadMessages.map((doc) => {
          const messageData = doc.data();
          const updateFields = { isRead: true };

          if (IS_READ_BY_TEACHER in messageData && messageData.isReadByTeacher === false) {
            updateFields.isReadByTeacher = true;
          }
          if (IS_READ_BY_PARENT in messageData && messageData.isReadByParent === false) {
            updateFields.isReadByParent = true;
          }

          return doc.ref.update(updateFields);
        });

        await Promise.all(messageUpdates);

        const conversationRef = db.collection(CONVERSATION).doc(conversationId);
        const conversationDoc = await conversationRef.get();

        if (conversationDoc.exists) {
          const conversationData = conversationDoc.data();
          const lastMessage = conversationData?.lastMessage;

          if (lastMessage && lastMessage.user.id !== userRecord.id && lastMessage.isRead === false) {
            const lastMessageUpdate = { 'lastMessage.isRead': true };

            if (IS_READ_BY_TEACHER in lastMessage && lastMessage.isReadByTeacher === false) {
              lastMessageUpdate[`${LAST_MESSAGE}.${IS_READ_BY_TEACHER}`] = true;
            }
            if (IS_READ_BY_PARENT in lastMessage && lastMessage.isReadByParent === false) {
              lastMessageUpdate[`${LAST_MESSAGE}.${IS_READ_BY_TEACHER}`] = true;
            }

            await conversationRef.update(lastMessageUpdate);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  return {
    currentOpenConversationMessages,
    getMessagesFromAtom,
    isLoadingMesssages,
    setIsLoadingMesssages,
    markUnreadMessagesAsRead,
    messageAllParents,
    messageAllTeachers,
    sendMessageToSingle,
    setCurrentOpenConversationMessages,
    useCurrentConversationListener,
    useGetAllParentTeacherAdminConversations,
    useParentConversationsByIds,
  };
};

export default useAdminMessages;
